import { useTranslation } from "react-i18next";
import React, { useEffect, useState } from 'react';
import { connect } from 'react-redux';
import { Helmet } from 'react-helmet-async';
import Layout from 'Components/Layout';
import CardList from 'Components/Personal/CardList';
import { tokenStorage } from 'Modules/API';
import './Personal.css';
import './Cards.css';
const API_BASE = window.HMI_API_BASE || process.env.REACT_APP_API_URL || 'https://api.heresmyinfo.com/api';

function Cards({
  profile,
  profileLoading
}) {
  const {
    t
  } = useTranslation();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [data, setData] = useState(null);
  useEffect(() => {
    let mounted = true;
    const token = tokenStorage.getAuthToken();

    if (!token) {
      setError('Not logged in');
      setLoading(false);
      return;
    }

    fetch(`${API_BASE}/users/profile`, {
      headers: {
        Authorization: `Bearer ${token}`
      }
    }).then(res => {
      if (!res.ok) {
        return res.text().then(text => {
          throw new Error(`HTTP ${res.status}: ${text}`);
        });
      }

      return res.json();
    }).then(json => {
      if (mounted) setData(json);
    }).catch(err => {
      console.error('Cards: Error:', err);
      if (mounted) setError(err.message || 'Failed to load profile');
    }).finally(() => {
      if (mounted) setLoading(false);
    });
    return () => {
      mounted = false;
    };
  }, []);

  if (loading) {
    return <Layout>
        <Helmet>
          <title>{t("My Cards")}</title>
        </Helmet>
        <div className="cardPage">
          <div className="loading-state">
            <div className="loading-spinner">
              <div className="spinner"></div>
            </div>
            <h3>{t("Loading your cards...")}</h3>
            <p>{t("Please wait while we fetch your card information")}</p>
          </div>
        </div>
      </Layout>;
  }

  if (error) {
    return <Layout>
        <Helmet>
          <title>{t("My Cards")}</title>
        </Helmet>
        <div className="cardPage">
          <div className="error-state">
            <div className="error-icon"><span role="img" aria-label="warning">⚠️</span></div>
            <h3>{t("Unable to load cards")}</h3>
            <p>{t("Error:")}{error}</p>
            <button className="retry-button" onClick={() => window.location.reload()}>{t("Try Again")}</button>
          </div>
        </div>
      </Layout>;
  } // Prefer live Redux profile so toggles reflect immediately; fallback to API data


  // eslint-disable-next-line no-mixed-operators
  let freshProfile = profile && Array.isArray(profile.cards) && profile.cards.length > 0 ? profile : data && data.profile || profile;

  // Temporarily filter out Public card
  if (freshProfile && Array.isArray(freshProfile.cards)) {
    freshProfile = {
      ...freshProfile,
      cards: freshProfile.cards.filter(c => (c.name || '').toLowerCase() !== 'public')
    };
  }

  // Synthesize missing Personal card if only one card exists (usually Work)
  if (freshProfile && Array.isArray(freshProfile.cards) && freshProfile.cards.length === 1) {
    const only = freshProfile.cards[0];

    if (only && only.name === 'Work') {
      const personalProps = (freshProfile.properties || []).filter(p => ['givenName', 'familyName', 'emailAddresses', 'phoneNumbers'].includes(p.name));
      const personalCard = {
        objectId: (only.objectId || only.id || 'work_card_tmp').replace('work_card_', 'personal_card_'),
        name: 'Personal',
        properties: personalProps,
        order: 1,
        public: false,
        defaultCard: false
      };
      freshProfile = { ...freshProfile,
        cards: [only, personalCard]
      };
    }
  }

  return <Layout>
      <Helmet>
        <title>{t("My Cards")}</title>
      </Helmet>
      <div className="cardPage">
        <CardList profile={freshProfile} profileLoading={profileLoading} />
      </div>
    </Layout>;
}

const mapStateToProps = state => ({
  profile: state.auth.currentUser ? state.auth.currentUser.profile : null,
  profileLoading: state.auth.loading,
  parse: state.auth.parse
});

export default connect(mapStateToProps)(Cards);